// Typeahead destination picker. Wraps the design's .field-box so it matches the
// flight search form's AirportPicker exactly.
//
// The one thing to know: a hotel destination is a **(city name, country code)
// pair**, not an id. The supplier's rate search is keyed on the city's own
// spelling, so the string we send back has to be the one the suggestion carried
// — echoing the user's typing instead returns zero hotels with no error.
//
// Keyboard: ↑ ↓ move the active item, Enter selects, Esc closes.

import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { DestinationSuggestion } from "../lib/hotelTypes";

interface Props {
  label: string;
  value: DestinationSuggestion | null;
  onChange: (destination: DestinationSuggestion) => void;
  isActive: boolean;
  onActivate: () => void;
  onClose: () => void;
}

export function DestinationPicker(props: Props) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<DestinationSuggestion[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.isActive) {
      setQuery("");
      setSuggestions([]);
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [props.isActive]);

  useEffect(() => {
    if (!props.isActive) return;
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const tid = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.get<{ results: DestinationSuggestion[] }>(
          `/v1/hotels/destinations/autocomplete/?q=${encodeURIComponent(q)}`,
        );
        if (!cancelled) {
          setSuggestions(data.results || []);
          setActiveIdx(0);
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [query, props.isActive]);

  function pick(s: DestinationSuggestion) {
    props.onChange(s);
    props.onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
      return;
    }
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = suggestions[activeIdx];
      if (s) pick(s);
    }
  }

  return (
    <div
      className={`field-box${props.isActive ? " active" : ""}`}
      onClick={(e) => { e.stopPropagation(); if (!props.isActive) props.onActivate(); }}
    >
      <div className="label">{props.label}</div>
      {props.isActive ? (
        <input
          ref={inputRef}
          type="text"
          className="value-input"
          placeholder="City or country"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}
        />
      ) : (
        <div className="value">{props.value?.city_name || "Where to?"}</div>
      )}
      <div className="sub">{props.value?.country_name || "Pick a destination"}</div>

      {props.isActive && (suggestions.length > 0 || loading || query.trim().length >= 2) && (
        <div className="airport-suggest" onMouseDown={(e) => e.preventDefault()}>
          {loading && suggestions.length === 0 && (
            <div className="airport-suggest-item" style={{ color: "var(--ink-3)", cursor: "default" }}>
              <span className="code">…</span>
              <div className="info"><b>Searching…</b><span>looking up "{query}"</span></div>
            </div>
          )}
          {!loading && suggestions.length === 0 && query.trim().length >= 2 && (
            <div className="airport-suggest-item" style={{ color: "var(--ink-3)", cursor: "default" }}>
              <span className="code">—</span>
              <div className="info">
                <b>No matches</b>
                <span>try another spelling, or search the country name</span>
              </div>
            </div>
          )}
          {suggestions.map((s, i) => (
            <div
              key={`${s.city_name}-${s.country_code}`}
              className={`airport-suggest-item${i === activeIdx ? " active" : ""}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(s)}
            >
              <span className="code">{s.country_code}</span>
              <div className="info">
                <b>{s.city_name}</b>
                <span>{s.country_name}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
